import { CommonModule, getCurrencySymbol } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, EventEmitter, Input, Output, LOCALE_ID, Inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClientService, ClaimService, CommonService, ToastMessageService, RefundService, PaymentModeService, AuthSevices } from '@fitness-force/api';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { PayModeLevel1, PayModeLevel2, PaymentGatewaySupported, RefundMode, RefundReason, Status } from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { Claim, CreateRefundRequest, CreateResponse, ListPaymentModeLevel2CountryMappingResponse, ListPaymentModeMastersResponse, PaymentMethod, PaymentModeMastersResponse, PaymentSignUp, Payments, RefundData, RefundType } from '@fitness-force/models';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COUNTRY_MASTER_STATE } from 'apps/ff-frontend/src/app/state/app.state';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { InputNumberComponent } from 'libs/shared/src/lib/components/input-number/input-number.component';
import { DirectivesSharedModule } from 'libs/shared/src/lib/directives/directives.module';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, NotFoundError, Observable, catchError, of, retryWhen, scan, switchMap, throwError, timer } from 'rxjs';

@Component({
  selector: 'process-refund',
  templateUrl: './process-refund.component.html',
  styleUrls: ['./process-refund.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    CurrencyformatPipe,
    MessageModule,
    CheckboxModule,
    AssignedToComponent,
    ErrorMsgComponent,
    InputMaskModule,
    InputNumberModule,
    InputTextModule,
    RadioButtonModule,
    DatePickerModule,
    DirectivesSharedModule,
    InputNumberComponent,
    TranslateModule],
})
export class ProcessRefundComponent implements OnInit {
  @Input('visible') showRefundModal: boolean = false;
  @Input('refundData') refundData: RefundData = {} as RefundData;
  @Input("IsMinimumPaymentRequired") IsMinimumPaymentRequired: boolean = false;
  @Output() closeDialog: EventEmitter<{ closePopup: boolean, reloadData: boolean }> = new EventEmitter();
  claims: string[] = ['can_do_billing'];
  claim$: Observable<Claim[]> = new Observable();
  refundForm: UntypedFormGroup = new UntypedFormGroup({});
  currencySymbol$ = new BehaviorSubject<string>('');
  totalOneTimeCost$ = new BehaviorSubject<number>(0);
  totalAmtTobePaid: number = 0;
  minimumPaymentRequiredPerc: number = 100;
  isClientRepresentative = new BehaviorSubject(true);
  returnReasons = Object.entries(RefundReason).filter((key) => typeof key[1] != 'number').map(([key, value]) => ({ key, value }));
  btnLoading = new BehaviorSubject<boolean>(false);
  paymentModes$ = new BehaviorSubject<PaymentModeMastersResponse[]>([]);
  paymentModeMaster$: Observable<ListPaymentModeMastersResponse> = new Observable();
  paymentModeLevel2$: Observable<ListPaymentModeLevel2CountryMappingResponse> = new Observable();
  currentUserDetails: any;
  _selectedstaff = new BehaviorSubject<number>(0);

  refundConfirmModal:boolean=false;
  refundConfirmControl = new UntypedFormControl('');
  onlineRefundPayload:any;

  btnConfirmRefundLoading:boolean=false;

  constructor(
    private store: Store,
    public translate: TranslateService,
    private clientService: ClientService,
    private fb: UntypedFormBuilder,
    private claimService: ClaimService,
    private commonService: CommonService,
    private refundService: RefundService,
    private toastMessageService: ToastMessageService,
    private cd: ChangeDetectorRef,
    private paymentModeService: PaymentModeService,
    private authService: AuthSevices,
    @Inject(LOCALE_ID) public locale: string
  ) { }

  ngOnInit(): void {
    this.totalOneTimeCost$.next(this.refundData.amount);
    this.totalAmtTobePaid = this.refundData.amount;
    this.claim$ = this.claimService.GetClaim(environment.BASE_URL);
    this.currentUserDetails = this.authService.decodedToken;
    this._selectedstaff.next(Number(this.authService.loggedInStaffId));
    this.store.select(COUNTRY_MASTER_STATE).subscribe((country) => {
      this.currencySymbol$.next(getCurrencySymbol(
        country.currency_code,
        'narrow',
        this.locale
      ));
    });

    this.SetFormFields();
    this.BindOtherPaymentMode(this.refundData.tenantId);
  }

  SetFormFields() {
    this.refundForm = this.fb.group({
      comment: new UntypedFormControl('', [Validators.required]),
      reason: new UntypedFormControl('', [Validators.required]),
      sales_rep: new UntypedFormControl(this.currentUserDetails.id, [Validators.required]),
      otherPaymentMode: new UntypedFormControl('cash', [Validators.required]),
      //CASH
      frmCashAmount: new UntypedFormControl(this.commonService.ConvertAmountFromMinorCurrency(this.totalOneTimeCost$.value), [Validators.required, Validators.max(this.commonService.ConvertAmountFromMinorCurrency(this.totalOneTimeCost$.value))]),

      //Credit CARD
      frmCCNumber: new UntypedFormControl('', [Validators.maxLength(4)]),
      frmCCNetworkType: new UntypedFormControl('', []),
      frmCCExpiryDate: new UntypedFormControl('', []),
      frmCCAuthCode: new UntypedFormControl('', []),
      frmCCAmt: new UntypedFormControl(''),

      //Credit CARD
      frmDCNumber: new UntypedFormControl('', [Validators.maxLength(4)]),
      frmDCNetworkType: new UntypedFormControl(null, []),
      frmDCExpiryDate: new UntypedFormControl(null, []),
      frmDCAuthCode: new UntypedFormControl('', []),
      frmDCAmt: new UntypedFormControl(''),

      //CHEQUE
      frmChequeBankName: new UntypedFormControl('', []),
      frmChequeDate: new UntypedFormControl('', []),
      frmChequeAmount: new UntypedFormControl(''),

      //UPI
      frmUpiProvider: new UntypedFormControl(''),
      frmUpiVpaid: new UntypedFormControl(''),
      frmUpiAmount: new UntypedFormControl(''),

      frmOnlineAmount: new UntypedFormControl(''),
    });

    if (this.IsMinimumPaymentRequired) {
      this.refundForm.controls['frmCashAmount'].addValidators([
        Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(this.totalOneTimeCost$.value)))),
      ]);
    }

    this.refundForm.controls['otherPaymentMode'].valueChanges.subscribe(
      (val) => {
        this.clearPaymentValidator();

        this.totalOneTimeCost$.subscribe({
          next: (amount: number) => {
            var creditAmount = 0;
            let otherPaymentModeid = 0;
            if (val.toLowerCase().startsWith('otherpaymentmode_rb_')) {
              otherPaymentModeid = parseInt(
                val.toLowerCase().replace('otherpaymentmode_rb_', '')
              );
              val = 'otherpaymentmode';
            }
            switch (val) {
              case 'cash':
                this.frmCash_AmountFormControl.clearValidators();
                this.frmCash_AmountFormControl.addValidators([Validators.required]);
                this.frmCash_AmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(val) - creditAmount))),
                ]);
                if (this.IsMinimumPaymentRequired) {
                  this.frmCash_AmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(val) - creditAmount))),
                  ]);
                }
                this.frmCash_AmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                this.frmCash_AmountFormControl.updateValueAndValidity();
                break;
              case 'cheque':
                this.frmCheque_AmountFormControl.clearValidators();
                this.frmCheque_AmountFormControl.addValidators([
                  Validators.required,
                ]);
                this.frmCheque_AmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);
                if (this.IsMinimumPaymentRequired) {
                  this.frmCheque_AmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                  ]);
                }
                this.frmCheque_AmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                this.frmCheque_AmountFormControl.updateValueAndValidity();
                this.frmCheque_BankNameFormControl.addValidators([Validators.required]);
                this.frmCheque_DateFormControl.addValidators([Validators.required]);
                break;
              case 'upi':
                this.frmUpi_AmountFormControl.clearValidators();
                this.frmUpi_AmountFormControl.addValidators([Validators.required]);
                this.frmUpi_AmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);
                if (this.IsMinimumPaymentRequired) {
                  this.frmUpi_AmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                  ]);
                }
                this.frmUpi_AmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                this.frmUpi_AmountFormControl.updateValueAndValidity();
                this.frmUpiVpaidFormControl.addValidators([Validators.required]);
                this.frmUpiProviderFormControl.addValidators([Validators.required]);
                break;
              case 'debit':
                this.frmDC_AmountFormControl.clearValidators();
                this.frmDC_AmountFormControl.addValidators([Validators.required]);
                this.frmDC_AmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);

                if (this.IsMinimumPaymentRequired) {
                  this.frmDC_AmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                  ]);
                }
                this.frmDC_AmountFormControl.addValidators([
                  Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);
                this.frmDC_AmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                this.frmCC_AmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);

                if (this.IsMinimumPaymentRequired) {
                  this.frmCC_AmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                  ]);
                }
                this.frmCC_AmountFormControl.addValidators([
                  Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);

                this.frmCC_AmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                if (this.IsMinimumPaymentRequired) {
                  this.refundForm.controls['inp_otherPaymentMode_' + otherPaymentModeid].addValidators([
                    Validators.min(Math.max(0, (this.calculateMInAmount(this.commonService.ConvertAmountFromMinorCurrency(amount), this.minimumPaymentRequiredPerc) - creditAmount))),
                  ]);
                }
                this.refundForm.controls['inp_otherPaymentMode_' + otherPaymentModeid].addValidators([
                  Validators.max(Math.max(0, (this.calculateMInAmount(this.commonService.ConvertAmountFromMinorCurrency(amount), this.minimumPaymentRequiredPerc) - creditAmount))),
                ]);
                this.refundForm.controls['inp_otherPaymentMode_' + otherPaymentModeid].setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                if (this.refundForm.controls['inp_otherPaymentMode_refno' + otherPaymentModeid]) {
                  this.refundForm.controls['inp_otherPaymentMode_refno' + otherPaymentModeid].addValidators([Validators.required]);
                  this.refundForm.controls['inp_otherPaymentMode_refno' + otherPaymentModeid].updateValueAndValidity();
                }
                break;
              case 'online':
                this.frmOnlineAmountFormControl.clearValidators();
                this.frmOnlineAmountFormControl.addValidators([Validators.required]);
                this.frmOnlineAmountFormControl.addValidators([
                  Validators.max(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                ]);
                if (this.IsMinimumPaymentRequired) {
                  this.frmOnlineAmountFormControl.addValidators([
                    Validators.min(Math.max(0, (this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount))),
                  ]);
                }
                this.frmOnlineAmountFormControl.setValue((this.commonService.ConvertAmountFromMinorCurrency(amount) - creditAmount));
                //this.frmOnlineAmountFormControl.disable();
                this.frmOnlineAmountFormControl.updateValueAndValidity();
                break;
            }
          }
        });
      }
    );
  }

  BindOtherPaymentMode(tenant_id: number) {
    this.paymentModeMaster$ = this.paymentModeService.listPaymentMode(tenant_id);
    this.paymentModeMaster$.subscribe({
      next: (listpaymentMode: ListPaymentModeMastersResponse) => {
        CONSTANTS_DATA.PAYMENTMODE.IN.forEach((obj) => {
          listpaymentMode.items.push(obj as any);
        });

        this.paymentModes$.next(
          listpaymentMode.items.filter((e) => {
            return (
              (e.status == Status.Active ||
                e.status.toString() == Status[Status.Active]) &&
              (e.type == PayModeLevel1.Other ||
                e.type.toString() == PayModeLevel1[PayModeLevel1.Other])
            );
          })
        );
        this.createDymamicPaymentControl();
      },
      error: (error: any) => {
        this.paymentModes$.next([]);
      },
    });
  }

  createDymamicPaymentControl() {
    this.paymentModes$.subscribe({
      next: (paymentModeMastersResponse: PaymentModeMastersResponse[]) => {
        paymentModeMastersResponse.forEach(
          (value: PaymentModeMastersResponse) => {
            this.refundForm.addControl(
              'inp_otherPaymentMode_' + value.id.toString(),
              new UntypedFormControl(0, [])
            );
            if (this.checkPaymentModeTabbyTamara(value)) {
              this.refundForm.addControl('inp_otherPaymentMode_refno' + value.id.toString(), new UntypedFormControl('', []));
            }
          }
        );
      },
      error: (error) => {
        if (error instanceof NotFoundError) {
        } else {
        }
      },
    });
  }

  clearPaymentValidator() {
    this.frmCash_AmountFormControl.removeValidators([Validators.required]);
    this.frmCheque_AmountFormControl.removeValidators([Validators.required]);
    this.frmUpi_AmountFormControl.removeValidators([Validators.required]);
    this.frmDC_AmountFormControl.removeValidators([Validators.required]);
    this.frmCC_AmountFormControl.removeValidators([Validators.required]);
    this.frmUpiVpaidFormControl.removeValidators([Validators.required]);
    this.frmUpiProviderFormControl.removeValidators([Validators.required]);
    this.frmCheque_BankNameFormControl.removeValidators([Validators.required]);
    this.frmCheque_DateFormControl.removeValidators([Validators.required]);
    this.frmOnlineAmountFormControl.enable();
    this.frmOnlineAmountFormControl.removeValidators([Validators.required]);
    Object.keys(this.refundForm.controls).forEach((key) => {
      if (key.toLowerCase().startsWith('inp_otherpaymentmode_')) {
        this.refundForm.controls[key].removeValidators([Validators.required]);
      }
    });
  }

  ProcessRefund() {
    this.refundForm.markAllAsTouched();
    if (this.refundForm.valid) {
      this.btnLoading.next(true);
      let service: any;
      let createRefundRequest: CreateRefundRequest = {} as CreateRefundRequest;
      createRefundRequest.sales_rep_id = this.SalesRepFormControl.value.id;
      createRefundRequest.refund_reason = this.SalesReturnReasonFormControl.value.key;
      createRefundRequest.comments = this.SalesReturnReasonCommentFormControl.value;
      createRefundRequest.refund_date = new Date().toISOString();
      if (this.otherPaymentMethod == 'online') {
        createRefundRequest.offline_payment = this.getPaymentObject();
        createRefundRequest.refund_mode = RefundMode.Online;
      }
      else {
        createRefundRequest.offline_payment = this.getPaymentObject();
        createRefundRequest.refund_mode = RefundMode.Offline;
      }
      createRefundRequest.amount_in_minor_currency = createRefundRequest.offline_payment.amount_in_minor_currency;
       
      if(this.otherPaymentMethod == 'online')
      {
        this.refundBackToSource(createRefundRequest);
        return;
      }


      if (this.refundData.refundType == RefundType.Payment || this.refundData.refundType.toString() == RefundType[RefundType.Payment]) {
        service = this.refundService.CreateRefund(this.refundData.tenantId, this.refundData.clientId, this.refundData.primaryId, createRefundRequest);
      }
      else {
        service = this.refundService.CreateCreditBalanceRefund(this.refundData.tenantId, this.refundData.clientId, createRefundRequest);
      }
      service.pipe(
        retryWhen(errors =>
          errors.pipe(
            // Check for the specific error condition that should trigger a retry
            scan((count, error) => {
              if (
                count < 2 && // Retry only 2 times
                error &&
                error.originalError.error &&
                error.originalError.error[0] &&
                error.originalError.error[0].errorCode === 'fullpaymentrefund'
              ) {
                // Update the payload before retrying
                createRefundRequest.amount_in_minor_currency = createRefundRequest.offline_payment.rounded_amount_in_minor_currency!;
                createRefundRequest.offline_payment.amount_in_minor_currency = createRefundRequest.offline_payment.rounded_amount_in_minor_currency!;
                return count + 1; // Increment the retry count
              }
              // Do not retry for other errors or after 3 retries
              throw error;
            }, 0),
            switchMap((retryCount) => timer(100))
          )
        ),
        catchError((error) => {
          return throwError(error);
        })
      ).subscribe({
        next: async (responose: CreateResponse) => {
          this.translate.get('SALES_RETURN.MSG.REFUND_PROCESED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          await this.timeout(1000);
          this.btnLoading.next(false);
          this.closePopup(true);
          this.cd.detectChanges();
        },
        error: (error: any) => {
          this.btnLoading.next(false);
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        }
      })
    }
    else {
      Object.keys(this.refundForm.controls).forEach((field) => {
        const control = this.refundForm.get(field);
        control?.markAsDirty({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  closePopup(event: boolean) {
    this.showRefundModal = false;
    this.closeDialog.emit({ closePopup: true, reloadData: event });
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  getPaymentObject() {
    var mode = this.otherPaymentMethod;
    const paymentSignUp = {} as PaymentSignUp;
    var otherPaymentModeid = 0;
    paymentSignUp.vpa_id = '';
    paymentSignUp.upi_provider = '';
    paymentSignUp.network_type = '';
    paymentSignUp.card_number = '';
    paymentSignUp.bank_name = '';
    paymentSignUp.auth_code = '';
    paymentSignUp.cheque_date = new Date();
    paymentSignUp.exp_date = new Date();
    paymentSignUp.is_credit_balance_used = false;
    paymentSignUp.credit_balance_amount_in_minor_currency = 0;

    if (mode.toLowerCase().startsWith('otherpaymentmode_rb_')) {
      otherPaymentModeid = parseInt(
        mode.toLowerCase().replace('otherpaymentmode_rb_', '')
      );
      paymentSignUp.other_paymode_master_id = otherPaymentModeid;
      mode = 'otherpaymentmode';
    }

    switch (mode) {
      case 'cash':
        paymentSignUp.amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmount(
            this.frmCash_Amount
          );
        paymentSignUp.rounded_amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmountRounded(
            this.frmCash_Amount
          );
        paymentSignUp.paymode_level1 = PayModeLevel1.Cash;
        paymentSignUp.paymode_level2_id = PayModeLevel2.Cash;
        break;
      case 'cheque':
        paymentSignUp.amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmount(
            this.frmCheque_Amount
          );
        paymentSignUp.rounded_amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmountRounded(
            this.frmCheque_Amount
          );
        paymentSignUp.cheque_date = this.frmCheque_Date;
        paymentSignUp.bank_name = this.frmCheque_BankName;
        paymentSignUp.paymode_level1 = PayModeLevel1.Cheque;
        paymentSignUp.paymode_level2_id = PayModeLevel2.Cheque;
        break;
      case 'upi':
        paymentSignUp.paymode_level1 = PayModeLevel1.UPI;
        paymentSignUp.paymode_level2_id = PayModeLevel2.UPI;
        paymentSignUp.vpa_id = this.frmUpiVpaid;
        paymentSignUp.upi_provider = this.frmUpiProvider;
        paymentSignUp.amount_in_minor_currency = this.commonService.ConvertMinorCurrencyFromAmount(this.frmUpi_Amount);
        paymentSignUp.rounded_amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmountRounded(
            this.frmUpi_Amount
          );
        break;
      case 'debit':
        if (this.frmDC_ExpiryDate) {
          // Create a new date using the given year and month
          const expirationDate = new Date(Number(this.frmDC_ExpiryDate.toString().split('/')[1]), Number(this.frmDC_ExpiryDate.toString().split('/')[0]) - 1, 1);
          // Set the date to the last day of the month
          expirationDate.setMonth(expirationDate.getMonth() + 1);
          expirationDate.setDate(expirationDate.getDate() - 1);
          paymentSignUp.exp_date = expirationDate;
        } else {
          paymentSignUp.exp_date = null;
        }
        paymentSignUp.amount_in_minor_currency = this.commonService.ConvertMinorCurrencyFromAmount(this.frmDC_Amount);
        paymentSignUp.rounded_amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmountRounded(
            this.frmDC_Amount
          );
        paymentSignUp.network_type = this.frmDC_NetworkType;
        paymentSignUp.card_number = 'XXXX-XXXX-XXXX-' + this.frmDC_Number.toString();
        paymentSignUp.auth_code = this.frmDC_AuthCode.toString();
        paymentSignUp.paymode_level1 = PayModeLevel1.Card;
        paymentSignUp.paymode_level2_id = PayModeLevel2.Debit;
        break;
      case 'credit':
        if (this.frmCC_ExpiryDate) {
          // Create a new date using the given year and month
          const expirationDate = new Date(Number(this.frmCC_ExpiryDate.toString().split('/')[1]), Number(this.frmCC_ExpiryDate.toString().split('/')[0]) - 1, 1);
          // Set the date to the last day of the month
          expirationDate.setMonth(expirationDate.getMonth() + 1);
          expirationDate.setDate(expirationDate.getDate() - 1);
          paymentSignUp.exp_date = expirationDate;
        } else {
          paymentSignUp.exp_date = null;
        }

        paymentSignUp.amount_in_minor_currency = this.commonService.ConvertMinorCurrencyFromAmount(this.frmCC_Amount);
        paymentSignUp.rounded_amount_in_minor_currency = this.commonService.ConvertMinorCurrencyFromAmountRounded(
          this.frmCC_Amount
        );
        paymentSignUp.network_type = this.frmCC_NetworkType;
        paymentSignUp.card_number = 'XXXX-XXXX-XXXX-' + this.frmCC_Number.toString();
        paymentSignUp.auth_code = this.frmCC_AuthCode.toString();
        paymentSignUp.paymode_level1 = PayModeLevel1.Card;
        paymentSignUp.paymode_level2_id = PayModeLevel2.Credit;
        break;

      case 'online':
        paymentSignUp.amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmount(
            this.frmCash_Amount
          );
        paymentSignUp.rounded_amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmountRounded(
            this.frmCash_Amount
          );
        paymentSignUp.paymode_level1 = PayModeLevel1.Card;
        paymentSignUp.paymode_level2_id = PayModeLevel2.Credit;
        break;

      case 'otherpaymentmode':
        paymentSignUp.amount_in_minor_currency =
          this.commonService.ConvertMinorCurrencyFromAmount(
            this.refundForm.controls[
              'inp_otherPaymentMode_' + otherPaymentModeid
            ].value
          );
        paymentSignUp.rounded_amount_in_minor_currency = this.commonService.ConvertMinorCurrencyFromAmountRounded(
          this.refundForm.controls[
            'inp_otherPaymentMode_' + otherPaymentModeid
          ].value
        );
        paymentSignUp.paymode_level1 = PayModeLevel1.Other;
        paymentSignUp.auth_code = this.refundForm.controls['inp_otherPaymentMode_refno' + otherPaymentModeid] ? this.refundForm.controls['inp_otherPaymentMode_refno' + otherPaymentModeid].value : '';
        break;
    }
    return paymentSignUp;
  }

  get PaymentMethodDetails() {
    if(!this.refundData.paymentMethods)
    {
      return null;
    }
    let paymentData: any = {};
    var data = PayModeLevel1[this.refundData.paymentMethods.level1.toString() as keyof typeof PayModeLevel1]
    var level2 = PayModeLevel2[this.refundData.paymentMethods.level2.toString() as keyof typeof PayModeLevel2]
    switch (data) {
      case PayModeLevel1.Cash:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.ACH:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Adjustment:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Card:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: this.refundData.paymentMethods.masked_card_number })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });

        break;
      case PayModeLevel1.Cheque:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Emandate:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.NetBanking:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: this.refundData.paymentMethods.masked_account_number ? this.refundData.paymentMethods.masked_account_number : "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.UPI:
        paymentData.number = this.refundData.paymentMethods.vpa_id;
        break;
      case PayModeLevel1.Wallet:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Other:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.PayTo:
        paymentData.number = this.refundData.paymentMethods.pay_to_details;
        break;
    }
    return paymentData;
  }

  getPaymentAmount(): number {
    var mode = this.otherPaymentMethod;
    var otherPaymentModeid = 0;
    let amount: number = 0;

    if (mode.toLowerCase().startsWith('otherpaymentmode_rb_')) {
      otherPaymentModeid = parseInt(
        mode.toLowerCase().replace('otherpaymentmode_rb_', '')
      );
      mode = 'otherpaymentmode';
    }

    switch (mode) {
      case 'cash':
        amount = this.frmCash_Amount;
        break;
      case 'cheque':
        amount = this.frmCheque_Amount;
        break;
      case 'upi':
        amount = this.frmUpi_Amount;
        break;
      case 'debit':
        amount = this.frmDC_Amount
        break;
      case 'credit':
        amount = this.frmCC_Amount;
        break;
      case 'otherpaymentmode':
        amount = this.refundForm.controls['inp_otherPaymentMode_' + otherPaymentModeid].value;
        break;
      case 'online':
        amount = this.frmOnlineAmountFormControl.value;
        break;
    }
    return amount;
  }

  getTotalAmountInCurrency() {
    return this.commonService.ConvertAmountFromMinorCurrency(this.refundData.amount);
  }

  getRemainingAmount(billAmount: number, inputAmount: number) {
    return Math.max(0, (billAmount - inputAmount));
  }

  getRequiredAmount() {
    return this.commonService.ConvertAmountFromMinorCurrency(this.calculateMInAmount(this.totalAmtTobePaid, this.minimumPaymentRequiredPerc));
  }

  calculateMInAmount(givenAmount: number, percent: number): number {
    // Calculate the amount based on the percentage
    const amount = (givenAmount * percent) / 100;
    return amount;
  }

  getfrmCash_Amount() {
    return this.commonService.ConvertMinorCurrencyFromAmount(
      this.frmCash_Amount
    );
  }

  getfrmCheque_Amount() {
    return this.commonService.ConvertMinorCurrencyFromAmount(
      this.frmCheque_Amount
    );
  }

  getfrmUpi_Amount() {
    return this.commonService.ConvertMinorCurrencyFromAmount(
      this.frmUpi_Amount
    );
  }

  getfrmCC_Amount() {
    return this.commonService.ConvertMinorCurrencyFromAmount(this.frmCC_Amount);
  }

  getfrmDC_Amount() {
    return this.commonService.ConvertMinorCurrencyFromAmount(this.frmDC_Amount);
  }

  checkPaymentModeTabbyTamara(paymentMode: PaymentModeMastersResponse): boolean {
    if ((paymentMode.name_primary_language.toLowerCase() == 'tabby' || paymentMode.name_secondary_language.toLowerCase() == 'tabby')
      || (paymentMode.name_primary_language.toLowerCase() == 'tamara' || paymentMode.name_secondary_language.toLowerCase() == 'tamara')
      || (paymentMode.name_primary_language.toLowerCase() == 'spotii' || paymentMode.name_secondary_language.toLowerCase() == 'spotii')
      || (paymentMode.name_primary_language.toLowerCase() == 'online payment' || paymentMode.name_secondary_language.toLowerCase() == 'online payment')
      || (paymentMode.name_primary_language.toLowerCase() == 'madfu' || paymentMode.name_secondary_language.toLowerCase() == 'madfu')) {
      return true;
    }
    else return false;
  }

  getFormControlByName(fornControlname: string): UntypedFormControl {
    return this.refundForm.get(fornControlname) as UntypedFormControl;
  }

  getPaymentBalance(totalAmt: number, ctrl: number) {
    return this.commonService.ConvertMinorCurrencyFromAmount(
      this.commonService.ConvertAmountFromMinorCurrency(totalAmt) -
      this.refundForm.get('inp_otherPaymentMode_' + ctrl.toString())?.value
    );
  }

  refundBackToSource(createRefundRequest: CreateRefundRequest)
  {
    this.onlineRefundPayload = createRefundRequest;
    this.refundConfirmControl.reset();
    this.refundConfirmControl.setValidators([Validators.required, Validators.pattern('^REFUND$')]);
    this.refundConfirmControl.updateValueAndValidity();
    this.refundConfirmModal = true;
  }

  ProcessRefundConfirm(confirmed:boolean)
  {
    if(confirmed)
    {
      this.refundConfirmControl.markAsUntouched();
      if(this.refundConfirmControl.valid)
      {
        this.btnConfirmRefundLoading = true;
        this.refundService.CreateRefund(this.refundData.tenantId, this.refundData.clientId, this.refundData.primaryId, this.onlineRefundPayload).pipe(
          retryWhen(errors =>
            errors.pipe(
              // Check for the specific error condition that should trigger a retry
              scan((count, error) => {
                if (
                  count < 2 && // Retry only 2 times
                  error &&
                  error.originalError.error &&
                  error.originalError.error[0] &&
                  error.originalError.error[0].errorCode === 'fullpaymentrefund'
                ) {
                  // Update the payload before retrying
                  this.onlineRefundPayload.amount_in_minor_currency = this.onlineRefundPayload.offline_payment.rounded_amount_in_minor_currency!;
                  this.onlineRefundPayload.offline_payment.amount_in_minor_currency = this.onlineRefundPayload.offline_payment.rounded_amount_in_minor_currency!;
                  return count + 1; // Increment the retry count
                }
                // Do not retry for other errors or after 3 retries
                throw error;
              }, 0),
              switchMap((retryCount) => timer(100))
            )
          ),
          catchError((error) => {
            return throwError(error);
          })
        ).subscribe({
          next: async (responose: CreateResponse) => {
            this.translate.get('SALES_RETURN.MSG.REFUND_PROCESED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            await this.timeout(1000);
            this.btnConfirmRefundLoading = false;
            this.closePopup(true);
            this.refundConfirmModal = false; 
            this.refundConfirmControl.reset();
            this.cd.detectChanges();
          },
          error: (error: any) => {
            this.btnConfirmRefundLoading = false;
            this.refundConfirmModal = false; 
            this.refundConfirmControl.reset();
            this.toastMessageService.showError(error);
            this.cd.detectChanges();
          }
        })
      }
    }
    else
    {
      this.refundConfirmModal = false;
      this.refundConfirmControl.reset();
      this.refundConfirmControl.setValidators([Validators.required, Validators.pattern('^REFUND$')]);
      this.refundConfirmControl.updateValueAndValidity();
    }
  }

  get otherPaymentMethodFormControl(): UntypedFormControl {
    return this.refundForm.controls['otherPaymentMode'] as UntypedFormControl;
  }

  get otherPaymentMethod(): string {
    return this.otherPaymentMethodFormControl.value as string;
  }

  set otherPaymentMethod(val: string) {
    this.otherPaymentMethodFormControl.setValue(val);
  }

  //#region Credit Card

  get frmCC_NumberFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCCNumber'] as UntypedFormControl;
  }

  get frmCC_Number(): number {
    return this.frmCC_NumberFormControl.value as number;
  }

  set frmCC_Number(val: number) {
    this.frmCC_NumberFormControl.setValue(val);
  }

  get frmCC_NetworkTypeFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCCNetworkType'] as UntypedFormControl;
  }

  get frmCC_NetworkType(): string {
    return this.frmCC_NetworkTypeFormControl.value as string;
  }

  set frmCC_NetworkType(val: string) {
    this.frmCC_NetworkTypeFormControl.setValue(val);
  }

  get frmCC_CardExpiryFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCCExpiryDate'] as UntypedFormControl;
  }

  get frmCC_ExpiryDate(): string {
    return this.frmCC_CardExpiryFormControl.value as string;
  }

  set frmCC_ExpiryDate(val: string) {
    this.frmCC_CardExpiryFormControl.setValue(val);
  }

  get frmCC_AuthCodeFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCCAuthCode'] as UntypedFormControl;
  }

  get frmCC_AuthCode(): number {
    return this.frmCC_AuthCodeFormControl.value as number;
  }

  set frmCC_AuthCode(val: number) {
    this.frmCC_AuthCodeFormControl.setValue(val);
  }

  get frmCC_AmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCCAmt'] as UntypedFormControl;
  }

  get frmCC_Amount(): number {
    return this.frmCC_AmountFormControl.value as number;
  }

  set frmCC_Amount(val: number) {
    this.frmCC_AmountFormControl.setValue(val);
  }
  //#endregion Credit Card

  //#region Debit Card
  get frmDC_NumberFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmDCNumber'] as UntypedFormControl;
  }

  get frmDC_Number(): number {
    return this.frmDC_NumberFormControl.value as number;
  }

  set frmDC_Number(val: number) {
    this.frmDC_NumberFormControl.setValue(val);
  }

  get frmDC_NetworkTypeFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmDCNetworkType'] as UntypedFormControl;
  }

  get frmDC_NetworkType(): string {
    return this.frmDC_NetworkTypeFormControl.value as string;
  }

  get frmDC_CardExpiryFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmDCExpiryDate'] as UntypedFormControl;
  }

  get frmDC_ExpiryDate(): string {
    return this.frmDC_CardExpiryFormControl.value as string;
  }

  set frmDC_ExpiryDate(val: string) {
    this.frmDC_CardExpiryFormControl.setValue(val);
  }

  get frmDC_AuthCodeFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmDCAuthCode'] as UntypedFormControl;
  }

  get frmDC_AuthCode(): number {
    return this.frmDC_AuthCodeFormControl.value as number;
  }

  set frmDC_AuthCode(val: number) {
    this.frmDC_AuthCodeFormControl.setValue(val);
  }

  get frmDC_AmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmDCAmt'] as UntypedFormControl;
  }

  get frmDC_Amount(): number {
    return this.frmDC_AmountFormControl.value as number;
  }

  set frmDC_Amount(val: number) {
    this.frmDC_AmountFormControl.setValue(val);
  }
  //#endregion Debit Card

  //#region Cheque
  get frmCheque_BankNameFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmChequeBankName'] as UntypedFormControl;
  }

  get frmCheque_BankName(): string {
    return this.frmCheque_BankNameFormControl.value as string;
  }

  set frmCheque_BankName(val: string) {
    this.frmCheque_BankNameFormControl.setValue(val);
  }

  get frmCheque_DateFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmChequeDate'] as UntypedFormControl;
  }

  get frmCheque_Date(): Date {
    return this.frmCheque_DateFormControl.value as Date;
  }

  set frmCheque_Date(val: Date) {
    this.frmCheque_DateFormControl.setValue(val);
  }

  get frmCheque_AmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmChequeAmount'] as UntypedFormControl;
  }

  get frmCheque_Amount(): number {
    return this.frmCheque_AmountFormControl.value as number;
  }

  set frmCheque_Amount(val: number) {
    this.frmCheque_AmountFormControl.setValue(val);
  }
  //#endregion Debit Card

  //#region UPI

  get frmCash_AmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmCashAmount'] as UntypedFormControl;
  }

  get frmCash_Amount(): number {
    return this.frmCash_AmountFormControl.value as number;
  }

  set frmCash_Amount(val: number) {
    this.frmCash_AmountFormControl.setValue(val);
  }

  //#endregion UPI

  //#region Cash

  get frmUpi_AmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmUpiAmount'] as UntypedFormControl;
  }

  get frmOnlineAmountFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmOnlineAmount'] as UntypedFormControl;
  }

  get frmUpi_Amount(): number {
    return this.frmUpi_AmountFormControl.value as number;
  }

  set frmUpi_Amount(val: number) {
    this.frmUpi_AmountFormControl.setValue(val);
  }

  get frmUpiProviderFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmUpiProvider'] as UntypedFormControl;
  }

  get frmUpiProvider(): string {
    return this.frmUpiProviderFormControl.value as string;
  }

  set frmUpiProvider(val: string) {
    this.frmUpiProviderFormControl.setValue(val);
  }

  get frmUpiVpaidFormControl(): UntypedFormControl {
    return this.refundForm.controls['frmUpiVpaid'] as UntypedFormControl;
  }

  get frmUpiVpaid(): string {
    return this.frmUpiVpaidFormControl.value as string;
  }

  set frmUpiVpaid(val: string) {
    this.frmUpiVpaidFormControl.setValue(val);
  }

  get SalesReturnReasonFormControl(): UntypedFormControl {
    return this.refundForm.get('reason')! as UntypedFormControl;
  }

  get SalesReturnReasonCommentFormControl(): UntypedFormControl {
    return this.refundForm.get('comment')! as UntypedFormControl;
  }

  getSalesReturnFormControl(controlName: string): UntypedFormControl {
    return this.refundForm.get(controlName)! as UntypedFormControl;
  }

  get SalesRepFormControl(): UntypedFormControl {
    return this.refundForm.get('sales_rep')! as UntypedFormControl;
  }

  get getRefundType() {
    return RefundType;
  }

  get getPaymentGatewayType() {
    return PaymentGatewaySupported;
  }

  isPaymentgatewaySupported() {
    return this.refundData.paymentGateway != null
      && (
        this.refundData.paymentGateway == PaymentGatewaySupported.PayenginCanada || this.refundData.paymentGateway.toString() == PaymentGatewaySupported[PaymentGatewaySupported.PayenginCanada]
        || this.refundData.paymentGateway == PaymentGatewaySupported.PayenginUSA || this.refundData.paymentGateway.toString() == PaymentGatewaySupported[PaymentGatewaySupported.PayenginUSA]
        || this.refundData.paymentGateway == PaymentGatewaySupported.PayenginePhilippines || this.refundData.paymentGateway.toString() == PaymentGatewaySupported[PaymentGatewaySupported.PayenginePhilippines]
      )
  }

  getSalesReturnAmountTotal() {
    return this.commonService.ConvertMinorCurrencyFromAmount(this.frmOnlineAmountFormControl.value);
  }
}